import {InjectionToken} from '@angular/core';
import {routerReducer, RouterReducerState} from '@ngrx/router-store';
import {
  Action,
  ActionReducer,
  ActionReducerMap,
  INIT,
  MetaReducer,
} from '@ngrx/store';
import {environment} from '../../environments/environment';
import {
  AddOnState,
  initialState,
  reducer as addonReducer,
} from '../add-ons/reducers/add-on.reducer';
import {addOnFeatureKey} from '../add-ons/reducers';
import {
  catalogFeatureKey,
  catalogInitialState,
  catalogReducer,
  CatalogState,
} from '../common/catalog/catalog.reducer';
import {
  cartFeatureKey,
  CartState,
  initialState as cartInitialState,
  reducer as cartReducer,
} from '../common/shopping-cart/cart.reducer';
import * as contextActions from '../common/context-data/context-data.actions';
import {
  ContextDataState,
  initialState as contextInitialState,
  reducer as contextReducer,
} from '../common/context-data/context-data.reducer';
import {contextDataKey} from '../common/context-data/context-data.selectors';
import {
  CurrentUserState,
  initialState as currentUserInitialState,
  reducer as currentUserReducer,
} from '../common/current-user/current-user.reducer';
import {currentUserFeatureKey} from '../common/current-user/current-user.selectors';
import {
  errorHandlingFeatureKey,
  ErrorState,
  initialErrorState,
  reducer as errorReducer,
} from '../common/error-handling/error-handling.reducer';
import {
  addressFeatureKey,
  AddressState,
  initialState as addressInitialState,
  reducer as addressReducer,
} from '../tax-and-shipping/address.reducer';

export interface AppState {
  router: RouterReducerState;
  [contextDataKey]: ContextDataState;
  [currentUserFeatureKey]: CurrentUserState;
  [cartFeatureKey]: CartState;
  [addOnFeatureKey]: AddOnState;
  [errorHandlingFeatureKey]: ErrorState;
  [catalogFeatureKey]: CatalogState;
  [addressFeatureKey]: AddressState;
}

export const ROOT_REDUCERS: InjectionToken<ActionReducerMap<AppState, Action>> =
  new InjectionToken<ActionReducerMap<AppState, Action>>(
    'Root reducers token',
    {
      factory: () => ({
        router: routerReducer,
        'context-feature': contextReducer,
        [currentUserFeatureKey]: currentUserReducer,
        cart: cartReducer,
        'add-on-feature': addonReducer,
        [errorHandlingFeatureKey]: errorReducer,
        [catalogFeatureKey]: catalogReducer,
        [addressFeatureKey]: addressReducer,
      }),
    },
  );

const appInitialState: AppState = {
  router: {} as RouterReducerState,
  [contextDataKey]: contextInitialState,
  [currentUserFeatureKey]: currentUserInitialState,
  [cartFeatureKey]: cartInitialState,
  [addOnFeatureKey]: initialState,
  [errorHandlingFeatureKey]: initialErrorState,
  [catalogFeatureKey]: catalogInitialState,
  [addressFeatureKey]: addressInitialState,
};

export function resetState(
  reducer: ActionReducer<AppState>,
): ActionReducer<AppState> {
  return (state, action) => {
    if (action !== null && action.type === contextActions.resetState.type) {
      // Preserve host-application configuration values across state resets.
      // enableCart and enableAddressCollection are set by the embedding app
      // (e.g. Motion) via HTML attributes or the CartCompanion component and
      // must not be reverted to their initial-state defaults.
      const preservedState: AppState = {
        ...appInitialState,
        [cartFeatureKey]: {
          ...appInitialState[cartFeatureKey],
          enableCart:
            state?.[cartFeatureKey]?.enableCart ??
            appInitialState[cartFeatureKey].enableCart,
        },
        [addressFeatureKey]: {
          ...appInitialState[addressFeatureKey],
          enableAddressCollection:
            state?.[addressFeatureKey]?.enableAddressCollection ??
            appInitialState[addressFeatureKey].enableAddressCollection,
        },
      };
      return reducer(preservedState, {type: INIT});
    }
    return reducer(state, action);
  };
}

const productionMetaReducers: MetaReducer<AppState>[] = [resetState];
const developmentMetaReducers: MetaReducer<AppState>[] = [resetState];

export const metaReducers: MetaReducer<AppState>[] = !environment.production
  ? developmentMetaReducers
  : productionMetaReducers;
